import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '../session'
import { jwtClient } from '../trpc'
import { AuthLayout, AuthLoading } from '../components/AuthLayout'
import { useT } from '../i18n'

// Web-reachable account deletion (ISSUE-2026-07-20-240, ADR 0091). Google Play / App Store require a
// deletion path reachable from the web without installing the app. This mirrors the in-app Perfil flow:
// a signed-in owner confirms, we call the Identity JWT face `scheduleAccountDeletion` (owner resolved
// from the bearer — never a client-supplied id), which soft-deletes into the grace window. On success we
// hand off to /logout, which clears the bridge cookie + tokens. This URL is what Play Console declares.
type Stage = 'idle' | 'confirm' | 'done'

export function DeleteAccountPage() {
  const { account, token, isLoading } = useSession()
  const t = useT()
  const navigate = useNavigate()
  const [stage, setStage] = useState<Stage>('idle')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Unauthenticated → sign in first, returning here afterwards (SignInPage honors a relative returnTo).
  // Guarded on `done`: once deletion succeeds the session is intentionally torn down, and we must NOT
  // bounce to sign-in — the success card stands until the user hands off to /logout.
  useEffect(() => {
    if (!isLoading && !account && stage !== 'done') {
      navigate('/signin?returnTo=/delete-account', { replace: true })
    }
  }, [account, isLoading, stage, navigate])

  if (isLoading || (!account && stage !== 'done')) {
    return <AuthLoading title={t.signingIn} />
  }

  const confirmDeletion = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await jwtClient(token).scheduleAccountDeletion.mutate()
      setStage('done')
    } catch {
      setError(t.deleteError)
    } finally {
      setSubmitting(false)
    }
  }

  if (stage === 'done') {
    return (
      <AuthLayout title={t.deleteDoneTitle} subtitle={t.deleteDoneBody}>
        <button
          className="t-body-strong rounded-full py-3 text-white"
          style={{ background: 'var(--clay)' }}
          onClick={() => navigate('/logout', { replace: true })}>
          {t.deleteDoneCta}
        </button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t.deleteTitle} subtitle={t.deleteIntro}>
      {account && (
        <p className="t-caption text-center" style={{ color: 'var(--txt3)' }}>
          {account.email}
        </p>
      )}

      {stage === 'idle' ? (
        <button
          className="t-body-strong rounded-full border py-3"
          style={{ borderColor: 'var(--clay)', color: 'var(--clay)' }}
          onClick={() => setStage('confirm')}>
          {t.deleteButton}
        </button>
      ) : (
        <>
          <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--clay)' }}>
            <p className="t-body-strong" style={{ color: 'var(--clay)' }}>
              {t.deleteConfirmTitle}
            </p>
            <p className="t-caption mt-1.5" style={{ color: 'var(--txt2)' }}>
              {t.deleteConfirmBody}
            </p>
          </div>

          {error && (
            <p className="t-caption text-center" style={{ color: 'var(--clay)' }}>
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <button
              className="t-body-strong flex-1 rounded-full border py-3 disabled:opacity-50"
              style={{ borderColor: 'rgba(var(--brd), 0.2)', color: 'var(--txt)' }}
              disabled={submitting}
              onClick={() => setStage('idle')}>
              {t.deleteKeepCta}
            </button>
            <button
              className="t-body-strong flex-1 rounded-full py-3 text-white disabled:opacity-50"
              style={{ background: 'var(--clay)' }}
              disabled={submitting}
              onClick={confirmDeletion}>
              {t.deleteConfirmCta}
            </button>
          </div>
        </>
      )}
    </AuthLayout>
  )
}
